import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { furnitureApi, exportApi, furnitureTypesApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCascadeLocation } from '../hooks/useCascadeLocation';
import { Package, Search, Plus, Eye, Edit, Trash2, ChevronLeft, ChevronRight, X, MapPin, Download, Warehouse } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent } from '../components/ui/collapsible';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  neuf: { label: 'Neuf', class: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  bon: { label: 'Bon', class: 'bg-blue-50 text-blue-700 border-blue-200' },
  usage: { label: 'Usagé', class: 'bg-amber-50 text-amber-700 border-amber-200' },
  hors_service: { label: 'Hors service', class: 'bg-rose-50 text-rose-700 border-rose-200' },
  en_maintenance: { label: 'En maintenance', class: 'bg-orange-50 text-orange-700 border-orange-200' }
};

const FurnitureListPage = () => {
  const [furniture, setFurniture] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStock, setFilterStock] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [locationFiltersOpen, setLocationFiltersOpen] = useState(false);
  const { isGestionnaire, isAdmin } = useAuth();
  const navigate = useNavigate();
  const LIMIT = 20;

  // Furniture types (dynamic from API)
  const [furnitureTypes, setFurnitureTypes] = useState([]);
  const [typeLabels, setTypeLabels] = useState({});

  // Location cascade
  const {
    sites, batiments, etages, bureaux,
    selectedSite, selectedBatiment, selectedEtage, selectedBureau,
    selectedLocationId, locationPath,
    setSelectedSite, setSelectedBatiment, setSelectedEtage, setSelectedBureau,
    loadSites,
  } = useCascadeLocation();

  useEffect(() => {
    loadSites();
    loadFurnitureTypes();
  }, []);

  // Load furniture types dynamically
  const loadFurnitureTypes = async () => {
    try {
      const response = await furnitureTypesApi.getAll();
      setFurnitureTypes(response.data);
      const labels = response.data.reduce((acc, ft) => {
        acc[ft.code] = ft.label;
        return acc;
      }, {});
      setTypeLabels(labels);
    } catch (error) {
      console.error('Erreur chargement types mobilier:', error);
    }
  };

  useEffect(() => {
    setPage(0);
    loadFurniture(0);
  }, [search, filterType, filterStatus, filterStock, selectedSite, selectedBatiment, selectedEtage, selectedBureau]);

  const loadFurniture = async (pageNum = page) => {
    setLoading(true);
    try {
      const params = { skip: pageNum * LIMIT, limit: LIMIT };
      if (search) params.search = search;
      if (filterType) params.type_mobilier = filterType;
      if (filterStatus) params.etat = filterStatus;
      if (filterStock) {
        params.en_stock = true;
      } else if (selectedLocationId) {
        params.location_id = selectedLocationId;
      }

      const response = await furnitureApi.getAll(params);
      setFurniture(response.data);
      setHasMore(response.data.length === LIMIT);
    } catch (error) {
      console.error('Erreur chargement mobilier:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.item) return;
    try {
      await furnitureApi.delete(deleteDialog.item.id);
      setDeleteDialog({ open: false, item: null });
      loadFurniture();
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = {};
      if (filterType) params.type_mobilier = filterType;
      if (filterStatus) params.etat = filterStatus;
      if (selectedSite) params.site_id = selectedSite;

      const response = await exportApi.furniture(params);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `export_mobilier_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      toast.success('Export CSV téléchargé');
    } catch (error) {
      console.error('Erreur export:', error);
      toast.error("Erreur lors de l'export");
    }
  };

  const clearFilters = () => {
    setSearch('');
    setFilterType('');
    setFilterStatus('');
    setFilterStock(false);
    setSelectedSite('');
  };

  const clearLocationFilters = () => setSelectedSite('');

  const hasFilters = search || filterType || filterStatus || filterStock || selectedSite;
  const hasLocationFilter = !!(selectedSite || selectedBatiment || selectedEtage || selectedBureau);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="furniture-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Mobilier</h1>
          <p className="text-slate-500 mt-1">Gérez votre parc mobilier</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />Export CSV
          </Button>
          {isGestionnaire() && (
            <Button onClick={() => navigate('/furniture/new')} className="bg-blue-600 hover:bg-blue-700 btn-scale" data-testid="add-furniture-btn">
              <Plus className="w-4 h-4 mr-2" />Ajouter
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par code, marque, modèle..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={filterType || 'all'} onValueChange={(v) => setFilterType(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[140px]" data-testid="filter-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {furnitureTypes.map(ft => (
                    <SelectItem key={ft.code} value={ft.code}>{ft.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[140px]" data-testid="filter-status">
                  <SelectValue placeholder="État" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les états</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant={filterStock ? 'secondary' : 'outline'}
                onClick={() => {
                  const next = !filterStock;
                  setFilterStock(next);
                  if (next) {
                    setSelectedSite('');
                    setLocationFiltersOpen(false);
                  }
                }}
                className="gap-2"
                data-testid="filter-stock-btn"
              >
                <Warehouse className="w-4 h-4" />
                En stock
              </Button>

              <Button
                variant={locationFiltersOpen ? 'secondary' : 'outline'}
                onClick={() => {
                  setLocationFiltersOpen(!locationFiltersOpen);
                  if (!locationFiltersOpen) setFilterStock(false);
                }}
                className="gap-2"
                disabled={filterStock}
              >
                <MapPin className="w-4 h-4" />
                Localisation
                {hasLocationFilter && <Badge className="bg-blue-600 text-white ml-1 px-1.5 py-0">!</Badge>}
              </Button>

              {hasFilters && (
                <Button variant="ghost" size="icon" onClick={clearFilters}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Location Filters - Collapsible */}
          <Collapsible open={locationFiltersOpen} onOpenChange={setLocationFiltersOpen}>
            <CollapsibleContent>
              <div className="pt-4 border-t space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    Filtrer par localisation
                  </h4>
                  {hasLocationFilter && (
                    <Button variant="ghost" size="sm" onClick={clearLocationFilters} className="h-7 text-xs">
                      <X className="w-3 h-3 mr-1" />Effacer
                    </Button>
                  )}
                </div>

                {locationPath && (
                  <div className="text-sm text-blue-600 font-medium bg-blue-50 px-3 py-2 rounded-md">
                    {locationPath}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-slate-500">Site</label>
                    <Select value={selectedSite} onValueChange={setSelectedSite}>
                      <SelectTrigger data-testid="filter-location-site">
                        <SelectValue placeholder="Tous les sites" />
                      </SelectTrigger>
                      <SelectContent>
                        {sites.map(site => (
                          <SelectItem key={site.id} value={site.id}>{site.nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-slate-500">Bâtiment</label>
                    <Select value={selectedBatiment} onValueChange={setSelectedBatiment} disabled={!selectedSite || batiments.length === 0}>
                      <SelectTrigger data-testid="filter-location-batiment">
                        <SelectValue placeholder={batiments.length === 0 ? '—' : 'Tous'} />
                      </SelectTrigger>
                      <SelectContent>
                        {batiments.map(b => (
                          <SelectItem key={b.id} value={b.id}>{b.nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-slate-500">Étage</label>
                    <Select value={selectedEtage} onValueChange={setSelectedEtage} disabled={!selectedBatiment || etages.length === 0}>
                      <SelectTrigger data-testid="filter-location-etage">
                        <SelectValue placeholder={etages.length === 0 ? '—' : 'Tous'} />
                      </SelectTrigger>
                      <SelectContent>
                        {etages.map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-slate-500">Bureau</label>
                    <Select value={selectedBureau} onValueChange={setSelectedBureau} disabled={!selectedEtage || bureaux.length === 0}>
                      <SelectTrigger data-testid="filter-location-bureau">
                        <SelectValue placeholder={bureaux.length === 0 ? '—' : 'Tous'} />
                      </SelectTrigger>
                      <SelectContent>
                        {bureaux.map(b => (
                          <SelectItem key={b.id} value={b.id}>{b.nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : furniture.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Aucun mobilier trouvé</h3>
              <p className="text-slate-500 mt-1">{hasFilters ? 'Essayez de modifier vos filtres' : 'Commencez par ajouter du mobilier'}</p>
              {!hasFilters && isGestionnaire() && (
                <Button onClick={() => navigate('/furniture/new')} className="mt-4 bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />Ajouter du mobilier
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th className="w-16">Photo</th>
                    <th>Code-barres</th>
                    <th>Type</th>
                    <th>Marque / Modèle</th>
                    <th>Localisation</th>
                    <th>État</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {furniture.map((item) => (
                    <tr key={item.id} className="table-row-hover" data-testid={`furniture-row-${item.id}`}>
                      <td>
                        {item.catalog_image_url ? (
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 border">
                            <img src={item.catalog_image_url} alt="" className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center border">
                            <Package className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td><span className="barcode-text">{item.code_barres}</span></td>
                      <td>{typeLabels[item.type_mobilier] || item.type_mobilier}</td>
                      <td>
                        <div>
                          <p className="font-medium text-slate-700">{item.marque || '-'}</p>
                          <p className="text-xs text-slate-500">{item.modele || ''}</p>
                        </div>
                      </td>
                      <td>
                        {item.location_path ? (
                          <span className="text-sm text-slate-600">{item.location_path}</span>
                        ) : item.site_nom ? (
                          <span className="text-sm text-slate-600">{item.site_nom}</span>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                      <td>
                        <Badge className={STATUS_CONFIG[item.etat]?.class || ''}>
                          {STATUS_CONFIG[item.etat]?.label || item.etat}
                        </Badge>
                      </td>
                      <td className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">Actions</Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/furniture/${item.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />Voir détail
                            </DropdownMenuItem>
                            {isGestionnaire() && (
                              <DropdownMenuItem onClick={() => navigate(`/furniture/${item.id}/edit`)}>
                                <Edit className="w-4 h-4 mr-2" />Modifier
                              </DropdownMenuItem>
                            )}
                            {isAdmin() && (
                              <DropdownMenuItem onClick={() => setDeleteDialog({ open: true, item })} className="text-rose-600">
                                <Trash2 className="w-4 h-4 mr-2" />Supprimer
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {furniture.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <Button
                variant="outline" size="sm"
                onClick={() => { const p = page - 1; setPage(p); loadFurniture(p); }}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />Précédent
              </Button>
              <span className="text-sm text-slate-500">Page {page + 1}</span>
              <Button
                variant="outline" size="sm"
                onClick={() => { const p = page + 1; setPage(p); loadFurniture(p); }}
                disabled={!hasMore}
              >
                Suivant<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer ce mobilier ?<br />
              <span className="font-mono text-slate-700">{deleteDialog.item?.code_barres}</span><br />
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null })}>Annuler</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FurnitureListPage;
